import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { CheckCircle, XCircle, EnvelopeSimple } from "@phosphor-icons/react";
import logo from "@/assets/logo_h_3d.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) { setStatus("error"); setErrorMsg("No verification token found in the link."); return; }

    (async () => {
      try {
        const res = await fetch(`${API}/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) { setStatus("error"); setErrorMsg(data.detail || "Verification failed."); return; }
        if (data.session_token) localStorage.setItem("session_token", data.session_token);
        const { session_token, ...user } = data;
        setUser(user);
        setStatus("success");
        setTimeout(() => navigate("/dashboard", { replace: true }), 2000);
      } catch {
        setStatus("error");
        setErrorMsg("Something went wrong. Please try again.");
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendLoading(true);
    try {
      await fetch(`${API}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resendEmail }),
      });
      setResendDone(true);
    } catch {
      setResendDone(true);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 text-center space-y-5">
          <img src={logo} alt="HostFolio" className="w-14 h-14 object-contain mx-auto" />

          {status === "verifying" && (
            <>
              <div className="w-14 h-14 mx-auto bg-amber-50 rounded-full flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-amber-400/40 border-t-amber-400 rounded-full animate-spin" />
              </div>
              <p className="text-slate-600 text-sm font-medium">Verifying your email…</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-14 h-14 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                <CheckCircle size={30} weight="fill" className="text-green-500" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-800">Email verified!</h2>
                <p className="text-xs text-slate-500 mt-1">Taking you to your dashboard…</p>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-14 h-14 mx-auto bg-red-50 rounded-full flex items-center justify-center">
                <XCircle size={30} weight="fill" className="text-red-400" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-800">Link invalid or expired</h2>
                <p className="text-xs text-slate-500 mt-1">{errorMsg}</p>
              </div>

              {resendDone ? (
                <div className="flex items-center gap-2 justify-center text-xs text-green-600 bg-green-50 rounded-xl px-4 py-3">
                  <CheckCircle size={14} weight="fill" />
                  Verification email sent — check your inbox
                </div>
              ) : (
                <div className="space-y-2 pt-1">
                  <p className="text-xs text-slate-400">Enter your email to get a new link:</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <EnvelopeSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={resendEmail}
                        onChange={e => setResendEmail(e.target.value)}
                        className="w-full pl-8 pr-3 py-2 text-xs rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-amber-400"
                      />
                    </div>
                    <button
                      onClick={handleResend}
                      disabled={resendLoading || !resendEmail}
                      className="px-3 py-2 bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
                    >
                      {resendLoading ? "…" : "Send"}
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => navigate("/login")}
                className="w-full text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                ← Back to sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
